import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import CommentList from "./comentarios/ComentarioList";
import CommentForm from "./comentarios/CommentForm";
import RedesSocialesProfe from "../../forms/subcribirseForm/RedesSocialesProfe";
import { Horario, PerfilData, TokenPayload } from "@/types";
import { ButtonSiguiente } from "../../ui";
import { SvgCorreo, SvgPaginaWeb, SvgTelefono } from "../../../icons";
import ResumenValoraciones from "./comentarios/TotalValoraciones";
import CarruselImgTrabajos from "./carruselImgTrabajos/CarruselImgTrabajos";
import { useSearch } from "../cardsBlock/buscarFiltrar/SearchContext";
import { safeFetch } from "@/api/safeFetch";
import { buildImageUrl } from "@/api/buildImageUrl";
import { PopupVerHorarios } from "@/components/popup";
import SvgHorario from "@/icons/SvgHorarios";
import TooltipLogBlock from "@/tooltip/TooltipLogBlock";
import { getEstadoHorario } from "@/components/utils/horarios";
import { useNavigate } from "react-router-dom";

const ProfesionalBlock: React.FC = () => {
  const [datosProfesional, setDatosProfesional] = useState<PerfilData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [nombreUsuario, setNombreUsuario] = useState<string | null>(null);
  const [showHorarios, setShowHorarios] = useState(false);
  const [horarios, setHorarios] = useState<Horario[]>([]);
  const { servicio_name, profesional_name } = useParams();
  const perfilId = datosProfesional?.perfil.perfil_profesional_id ?? 0;
  const { registerItem, unregisterItem } = useSearch();
  const estadoHorario = getEstadoHorario(horarios);
  const navigate = useNavigate();

  /* Activar Landing */
  const [landingPublica, setLandingPublica] = useState(false);

  const emailMostrar =
    datosProfesional?.perfil.correo_perfil || datosProfesional?.user.email;

  /*==================================
    CREAR VISIVIDAD O NO A LA LANDING
  ===================================*/
  useEffect(() => {
    const fetchEstado = async () => {
      const res = await safeFetch(`/api/crearLanding/${perfilId}/estado`);
      if (!res.ok) return;

      const data = await res.json();
      setLandingPublica(data.publica);
    };

    if (perfilId) fetchEstado();
  }, [perfilId]);

  // Cargar datos del profesional
  useEffect(() => {
    const fetchProfessionalData = async () => {
      try {
        const response = await safeFetch(
          `/api/profesional/${servicio_name}/${profesional_name}`,
        );
        if (!response.ok)
          throw new Error("Error al cargar los datos del profesional");
        const data: PerfilData = await response.json();
        setDatosProfesional(data);
      } catch (err: unknown) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    if (servicio_name && profesional_name) fetchProfessionalData();
  }, [servicio_name, profesional_name]);

  // Cargar horarios
  useEffect(() => {
    if (!perfilId) return;

    const fetchHorarios = async () => {
      try {
        const res = await safeFetch(`/api/horarios/${perfilId}`);
        if (!res.ok) return;
        const data: Horario[] = await res.json();

        setHorarios(data); // guardamos todos los horarios sin filtrar
      } catch (e) {
        console.error(e);
      }
    };

    fetchHorarios();
  }, [perfilId]);

  // Verificar token y autenticación
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode<TokenPayload>(token);
        const isExpired =
          decodedToken.exp && decodedToken.exp * 1000 < Date.now();
        if (isExpired) {
          localStorage.removeItem("token");
          setIsAuthenticated(false);
        } else {
          if (decodedToken.username) setNombreUsuario(decodedToken.username);
          setIsAuthenticated(true);
        }
      } catch {
        setIsAuthenticated(false);
      }
    } else setIsAuthenticated(false);
  }, []);

  // Registrar en buscador/contexto
  const [registered, setRegistered] = useState(false);
  useEffect(() => {
    if (!datosProfesional || registered) return;
    const id = `profesional-${datosProfesional.perfil.perfil_profesional_id}`;
    registerItem({
      id,
      title: [
        datosProfesional.perfil.nombre_comercial,
        datosProfesional.user.nombre_empresa,
      ]
        .filter(Boolean)
        .join(" "),
      content: [
        datosProfesional.perfil.descripcion,
        datosProfesional.perfil.nombre_comercial,
        datosProfesional.user.nombre_empresa,
      ]
        .filter(Boolean)
        .join(" "),
      type: "perfil",
      tags: [
        datosProfesional.perfil.nombre_comercial ?? "",
        datosProfesional.user.nombre_empresa ?? "",
      ],
    });
    setRegistered(true);
    return () => unregisterItem(id);
  }, [datosProfesional, registerItem, unregisterItem, registered]);

  function wrapText(text?: string, firstLine = 18, otherLines = 25) {
    if (!text) return null;

    const words = text.split(" ");
    const lines: string[] = [];

    let currentLine = "";
    let limit = firstLine;

    for (const word of words) {
      // Si la palabra sola es más larga que el límite, hay que partirla
      if (word.length > limit) {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = "";
        }

        // Partimos la palabra en trozos del tamaño del límite
        let start = 0;
        while (start < word.length) {
          lines.push(word.slice(start, start + limit));
          start += limit;
          limit = otherLines; // después de la primera línea
        }
      } else {
        const nextLine = currentLine ? currentLine + " " + word : word;

        if (nextLine.length > limit) {
          // la palabra no cabe en la línea actual → salto
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = nextLine;
        }
      }

      limit = lines.length === 0 ? firstLine : otherLines; // aseguramos límite correcto
    }

    if (currentLine) lines.push(currentLine);

    return lines.map((line, i) => (
      <span key={i}>
        {line}
        {i < lines.length - 1 && <br />}
      </span>
    ));
  }
  const handleCommentAdded = () => {};

  if (loading)
    return (
      <div className="text-center mt-10 text-zinc-500">
        Cargando perfil profesional...
      </div>
    );
  if (error)
    return <div className="text-center mt-10 text-red-500">Error: {error}</div>;
  if (!datosProfesional)
    return <div>No se encontraron datos del profesional.</div>;

  return (
    <div className="mb-10 mt-16 max-w-6xl mx-auto bg-zinc-200 rounded-lg shadow-2xl overflow-hidden text-zinc-600 ">
      {/* Cabecera */}
      <div className="grid grid-cols-1 mt-4 sm:grid-cols-2 md:grid-cols-3 w-full shadow-md border-b h-auto sm:h-24">
        {/* IZQUIERDA: nombre empresa */}
        <div className="flex items-center pl-4 order-1">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-zinc-900">
            {datosProfesional.perfil.nombre_comercial?.trim() ||
              datosProfesional.user.nombre_empresa}
          </h1>
        </div>

        {/* DERECHA: valoraciones (EN MÓVIL VA ARRIBA DEL BOTÓN) */}
        <div className="flex items-center justify-end pr-4 order-2 sm:order-3">
          <ResumenValoraciones profesionalId={perfilId} />
        </div>

        {/* CENTRO: botón centrado (EN MÓVIL VA DEBAJO DE TODO) */}
        <div className="flex items-center justify-start px-4 pt-4 order-3 sm:order-2 pb-2">
          {landingPublica && (
            <button
              onClick={() =>
                navigate(`/landing/${perfilId}`, {
                  state: {
                    perfilId,
                    from: `/profesional/${servicio_name}/${profesional_name}`,
                  },
                })
              }
              className="
              w-full
              h-10
              md:w-2/3
              rounded-lg
              bg-zinc-900/90
              border
              border-cyan-400/60
              text-cyan-300
              font-semibold
              tracking-wide
              shadow-[0_0_18px_rgba(34,211,238,0.18)]
              hover:bg-cyan-400/40
              hover:border-zinc-800
              hover:text-zinc-800
              transition-all
              duration-300
              flex
              items-center
              justify-center
              gap-2
            "
            >
              <span>Ver más</span>

              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-current text-xs ">
                +
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Información básica */}
      {/*  <p className="text-zinc-600 p-4"><strong>Nacionalidad: </strong>{datosProfesional.perfil.direccion?.pais}</p> */}

      <div className="px-4 grid grid-cols-4 gap-4 w-full h-auto pt-4">
        {/* Foto de perfil */}
        <div className="col-span-2 sm:col-span-1 rounded-lg border w-full h-36 overflow-hidden">
          <div className="relative w-full h-full flex items-center justify-center bg-black rounded-lg">
            <div
              className="absolute inset-0 bg-cover bg-center blur-lg opacity-70 scale-110"
              style={{
                backgroundImage: `url(${buildImageUrl(datosProfesional.perfil.foto_perfil)})`,
              }}
            />
            <img
              src={buildImageUrl(datosProfesional.perfil.foto_perfil)}
              alt="Logo del profesional"
              className="relative z-10 object-contain w-full h-full rounded-lg"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  "/img/logo/ImagenLogo.png";
              }}
            />
          </div>
        </div>

        {/* Dirección y Horarios */}
        <div className="flex flex-col justify-center col-span-2 sm:col-span-2 md:col-span-1">
          <p>
            <strong>Calle: </strong>{" "}
            {wrapText(datosProfesional.perfil.direccion?.calle, 18, 25)}
          </p>
          <p>
            <strong>Localidad: </strong>
            {datosProfesional.perfil.direccion?.localidad}
          </p>
          <p>
            <strong>C.P: </strong>
            {datosProfesional.perfil.direccion?.codigo_postal}
          </p>
          <p>
            <strong>Provincia: </strong>
            {datosProfesional.perfil.direccion?.provincia}
          </p>

          {horarios.length > 0 && (
            <TooltipLogBlock
              label={estadoHorario.texto}
              onClick={() => setShowHorarios(true)}
              place="top"
            >
              <div
                className={`mt-4 flex items-center gap-2 cursor-pointer transition ${estadoHorario.abierto ? "text-green-600" : "text-red-500"} hover:opacity-80`}
              >
                <SvgHorario className="w-6 h-6" />
                <span className="text-sm">Horarios</span>
              </div>
            </TooltipLogBlock>
          )}
        </div>

        {showHorarios && (
          <PopupVerHorarios
            horarios={horarios}
            onClose={() => setShowHorarios(false)}
          />
        )}

        {/* Contacto */}
        <div className="flex flex-col justify-center col-span-4 sm:col-span-4 md:col-span-1 space-y-2">
          <p className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center">
              <SvgTelefono className="w-8 h-8" />
            </span>
            <span className="text-sm">
              Tlf: {datosProfesional.user.numero_telefono}
            </span>
          </p>

          <p className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center">
              <SvgCorreo className="w-8 h-8" />
            </span>

            {emailMostrar ? (
              <a
                href={`mailto:${emailMostrar}`}
                className="text-sm text-blue-600 hover:text-green-600 transition-colors duration-200"
              >
                {emailMostrar}
              </a>
            ) : (
              <span className="text-sm">No disponible</span>
            )}
          </p>

          <p className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center">
              <SvgPaginaWeb className="w-10 h-10" />
            </span>
            {datosProfesional.perfil.url_web ? (
              <a
                href={datosProfesional.perfil.url_web}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white border-2 text-sm shadow-lg hover:shadow-blue-700 hover:shadow-md border-green-200 p-1 px-2 rounded-lg bg-zinc-600 hover:bg-white hover:text-black hover:border-zinc-700"
              >
                Visita nuestra web
              </a>
            ) : (
              <span className="text-zinc-600 text-sm">No dispone de web</span>
            )}
          </p>
        </div>

        {/* Redes Sociales */}
        <div className="flex flex-col items-center w-full col-span-4 sm:col-span-4 md:col-span-1">
          <label className="font-bold">Síguenos en nuestras redes:</label>
          {perfilId > 0 && (
            <RedesSocialesProfe
              perfil_id={perfilId}
              servicio_name={servicio_name ?? ""}
              profesional_name={profesional_name ?? ""}
            />
          )}
        </div>
      </div>

      {/* Descripción, carrusel y comentarios */}
      <div className="px-4 space-y-8">
        <div className="flex justify-center items-center w-full h-full pt-8">
          <div className="w-full space-y-8">
            <div className="border-4 border-double border-zinc-600"></div>
            <div className="bg-white border-t-2 border-r-zinc-100 border-r-4 border-zinc-400 rounded-lg shadow-xl p-4 text-center">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">
                {" "}
                {datosProfesional.perfil.nombre_comercial?.trim() ||
                  datosProfesional.user.nombre_empresa}
              </h2>
              <p className="text-zinc-600 text-start">
                {datosProfesional.perfil.descripcion}
              </p>
            </div>
          </div>
        </div>

        <div className="border-4 border-double border-zinc-600"></div>

        <div>
          {Boolean(perfilId) && perfilId > 0 && (
            <CarruselImgTrabajos
              source="api"
              profesionalId={perfilId}
              showNoImagesText
            />
          )}
          <CommentList
            perfil_id={perfilId}
            servicio_name={servicio_name ?? ""}
            profesional_name={profesional_name ?? ""}
            className="mt-8"
          />

          <div className="mt-8 border-t border-gray-200 py-8">
            {isAuthenticated ? (
              <div className="flex items-center justify-center">
                <ButtonSiguiente
                  className="mt-8 h-9 text-white rounded-md justify-center w-full bg-black shadow-md border-1 hover:border-2"
                  onClick={() => setShowCommentForm(true)}
                >
                  Añadir comentario
                </ButtonSiguiente>
              </div>
            ) : (
              <p className="mt-8 text-center text-red-500">
                Debes iniciar sesión para añadir un comentario.
              </p>
            )}

            {showCommentForm && nombreUsuario && (
              <CommentForm
                perfil_id={perfilId}
                nombre_usuario={nombreUsuario}
                onCommentAdded={handleCommentAdded}
                onClose={() => setShowCommentForm(false)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfesionalBlock;
